import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Quote, Globe, Users, Award, Instagram, Newspaper } from 'lucide-react';

const mediaProof = [
  {
    icon: Newspaper,
    title: 'Times of Africa',
    stat: '26 Nations',
    description: '10K+ circulation, 100+ leader interviews'
  },
  {
    icon: Instagram,
    title: '@businesswisdombykirit',
    stat: '50K+',
    description: 'Followers, daily content'
  },
  {
    icon: Award,
    title: 'World Bank PRCG 2014',
    stat: 'Recognition',
    description: 'Digital tax collection innovation'
  },
];

export const SocialProofSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section 
      id="proof" 
      className="section relative overflow-hidden"
      data-testid="social-proof-section"
    >
      <div className="max-w-7xl mx-auto px-6" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Trust Builder</p>
          <h2 className="section-title">
            Social Proof & <span className="gradient-text">Media Presence</span>
          </h2>
        </motion.div>

        {/* Testimonial Bridge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.2 }}
          className="glass rounded-2xl p-8 mb-12 text-center"
        >
          <Quote size={40} className="text-[#D4AF37] mx-auto mb-4 opacity-50" />
          <p className="text-xl text-white/80 italic font-['Playfair_Display'] mb-4">
            "In conversation with heads of state, ambassadors, and ministers across 26 African nations."
          </p>
          <p className="text-sm text-white/50">— Through The Times of Africa & 30 years of business relationships</p>
        </motion.div>

        {/* Media Proof Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {mediaProof.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-light rounded-2xl p-6 text-center card-hover"
            >
              <item.icon size={32} className="text-[#D4AF37] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
              <p className="text-2xl font-bold text-[#D4AF37] mb-2">{item.stat}</p>
              <p className="text-sm text-white/50">{item.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Instagram Feed Preview */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45] flex items-center justify-center">
                <Instagram size={24} className="text-white" />
              </div>
              <div>
                <p className="font-semibold text-white">@businesswisdombykirit</p>
                <p className="text-sm text-white/50">50K+ Followers • Daily Business Wisdom</p>
              </div>
            </div>
            <a
              href="https://instagram.com/businesswisdombykirit"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm"
            >
              Follow
            </a>
          </div>
          
          {/* Photo Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {[
              'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/3f9gyyzz_2585ef15-8017-4d06-a25c-b2ba89332fd7.jpeg',
              'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/xokytcsi_72173d55-7693-4779-8235-5b4657f58715.jpeg',
              'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/5bm5thtg_d7f53e28-0837-4280-93f5-f260909862bf.jpeg',
              'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/1f9sk8dn_bcd7b376-55c9-49a7-ac2c-8257160a9f88.jpeg',
              'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/rrbqihbo_63517d36-bf40-493b-a861-e54ad033472b.jpeg',
              'https://customer-assets.emergentagent.com/job_global-impact-10/artifacts/3f9gyyzz_2585ef15-8017-4d06-a25c-b2ba89332fd7.jpeg',
            ].map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.6 + index * 0.05 }}
                className="aspect-square rounded-lg overflow-hidden"
              >
                <img 
                  src={img} 
                  alt={`Business Wisdom ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
            ))}
          </div>

          {/* Topic Tags */}
          <div className="flex flex-wrap gap-2 mt-4 justify-center">
            {[
              { topic: 'Entrepreneurship', likes: '12.4K' },
              { topic: 'Africa Growth', likes: '8.7K' },
              { topic: 'Leadership', likes: '15.2K' },
              { topic: 'Innovation', likes: '9.8K' },
            ].map((tag) => (
              <span key={tag.topic} className="px-3 py-1 rounded-full text-xs bg-white/5 text-white/60">
                {tag.topic} • {tag.likes}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
